import Utils from "../../../../vendor_core/core/src/utils/Utils";
import SpinDataParser from "../../../../vendor_core/src/server/parsers/SpinDataParser";
import ISpinData from "../../../../vendor_core/core/src/server/data/ISpinData";
import GameModel from "../../../../vendor_core/core/src/model/GameModel";
import RoundType from "../../../../vendor_core/core/src/const/RoundType";
import BURSpinModel from "../../models/BURSpinModel";
import BURFeatureModel from "../../models/BURFeatureModel";
import {
    BURSpinData,
    MaybeUndefined,
    PointLike
} from "../../types/BURGameTypes";
import { IVendorSpinData } from "../../../../vendor_core/src/model/IVendorSpinData";
import { BURFreeSpinsData } from "../../types/IBURFeatureModel";
import ConsoleUtil from "../../../../vendor_core/core/src/utils/ConsoleUtil";
import DebugModel from "../../../../vendor_core/core/src/model/DebugModel";
import JackpotsModel from "../../../../vendor_core/src/model/JackpotsModel";
import { BonusName } from "../../../../vendor_core/src/server/data/BonusName";
import App from "../../../../vendor_core/core/src/App";
import BURReelsModel from "../../models/BURReelsModel";
import BURJackpotsModel from "../../models/BURJackpotsModel";
import { IMeterState } from "../../types/IBURReelsModel";
import SpinModel from "../../../../vendor_core/core/src/model/SpinModel";

export default class BURSpinDataParser extends SpinDataParser {
    protected gameSpecificFreespinTotalPerDrop: number = 8;
    private spinData: MaybeUndefined<BURSpinData> = undefined;

    protected validateData(data: any): boolean {
        if (data.data) {
            return (
                data.data.gameData?.view || data.data.lastSessionData?.betResult
            );
        }
        return data.view || data.lastSessionData?.betResult;
    }

    parseSlotObject(
        spinData: BURSpinData,
        isFree = false,
        mainSpinWin = 0,
        previousSpins?: ISpinData[],
        roundType?: RoundType | string
    ): ISpinData {
        this.resetFeatureData();

        this.spinData = spinData;

        const reelSetIndex = BURReelsModel.reelset_index;

        const stopPositions = new Map<number, number[]>();
        stopPositions.set(reelSetIndex, this.spinData.reelStops);

        const reelFinalSymbols = new Map<number, number[][]>();
        reelFinalSymbols.set(
            reelSetIndex,
            this.spinData.finalView || this.spinData.view
        );

        const reelStopSymbols = new Map<number, number[][]>();
        reelStopSymbols.set(reelSetIndex, this.spinData.view);

        // Mystery symbols
        const mysteryPositions: PointLike[] = [];

        this.spinData.view.map((reel, x) => {
            reel.map((sbl, y) => {
                if (
                    sbl === BURReelsModel.mystery_symbol_id ||
                    sbl === BURReelsModel.fs_mystery_symbol_id
                ) {
                    mysteryPositions.push({ x, y });
                }
            });
        });

        BURSpinModel.setMysteryPositions(mysteryPositions);

        // Cascades
        BURSpinModel.setCascades(this.spinData?.cascadeSteps ?? []);
        const hasCascades = BURSpinModel.cascades.length > 0;

        if (hasCascades) {
            BURSpinModel.setCurrentCascadeIndex(0);
        }

        // Base game features
        if (this.spinData.featureInfo?.stormWilds) {
            this.parseStormWildsData();
        }

        if (this.spinData.featureInfo?.energyCharge) {
            this.parseEnergyChargeData();
        }

        if (
            this.spinData.featureInfo?.blueReplayRespin &&
            this.spinData.replay
        ) {
            this.parseBlueRespinData();
        }

        // Free spins trigger data
        let freeSpinsData: MaybeUndefined<BURFreeSpinsData> = undefined;
        if (BURSpinModel.current_free_spin_index === -1) {
            freeSpinsData = this.spinData.bonusInfo?.find(
                (bonus) => bonus.bonusName === "BonusFreeSpins"
            );

            BURFeatureModel.setFreeSpinsData(freeSpinsData);

            const willFsTrigger = !!(
                freeSpinsData && freeSpinsData.freeSpins.length > 0
            );
            BURFeatureModel.setWillFreeSpinsTrigger(willFsTrigger);

            if (willFsTrigger) {
                BURFeatureModel.setFeatureHasStickyWilds(
                    freeSpinsData!.combo.some((c) => c === "Red")
                );
            }
        }

        // Wins
        // 1. Cascade wins
        let winAmount =
            this.spinData.cascadeSteps && this.spinData.cascadeSteps.length > 0
                ? this.spinData.cascadeSteps.reduce(
                      (winAcc, cascade) => winAcc + cascade.creditsWon,
                      0
                  )
                : 0;

        // 2. Total win
        if (this.spinData.spinStats) {
            BURSpinModel.setTotalWin(this.spinData.spinStats.totalWon);
        }

        // Energy meters
        // 1. Current progress
        BURReelsModel.setMetersProgress(this.spinData.featureInfo?.meters);

        // 2. Per-bet state
        if (this.spinData.userState) {
            BURReelsModel.setMeterState(
                this.spinData.userState as unknown as IMeterState[]
            );
        }

        // Jackpots
        // 1. JP fragments
        const fragments = this.spinData.jackpotReveals;
        if (fragments) {
            const symbolIds = hasCascades
                ? this.spinData.cascadeSteps![0].gridBefore
                : this.spinData.finalView!;
            BURJackpotsModel.setJackpotFragments(
                fragments.map((s) => ({
                    ...s,
                    symbolId: symbolIds[s.x][s.y]
                }))
            );
        }

        //2. JP wins
        if (this.spinData.jackpotWins) {
            BURJackpotsModel.setJackpotWins(this.spinData.jackpotWins);
        }

        return {
            winAmount,
            accumulatedWin: SpinModel.accumulatedWin + winAmount,
            stopPositions,
            reelFinalSymbols,
            reelStopSymbols,
            reelSets: Utils.deepClone(GameModel.gameConfig.reelSets),
            reelSetIndex,
            cascades: [], // cascades are game specific
            isFree,
            freespinsWon: BURFeatureModel.free_spins_data?.countFreeSpins,
            freespinsNum: BURSpinModel.current_free_spin_index + 1,
            totalFreeSpins: BURFeatureModel.free_spins_data?.countFreeSpins,
            freespinWinnings: 0,
            roundType
        };
    }

    parseStormWildsData() {
        BURFeatureModel.setWillBaseGameFeatureTrigger(true);
        BURFeatureModel.addBaseGameFeatureToTrigger("RandomWilds");
        BURFeatureModel.setRandomWildsPositions(
            this.spinData!.featureInfo!.stormWilds!.positions ?? []
        );
    }

    parseEnergyChargeData() {
        const data = this.spinData!.featureInfo!.energyCharge!;
        BURFeatureModel.setWillBaseGameFeatureTrigger(true);
        if (data.charge) {
            if (data.charge.kind === "BuffaloEnergyCharge") {
                // Buffalo Energy Charge feature
                BURFeatureModel.addBaseGameFeatureToTrigger("EnergyCoins");
                BURFeatureModel.setEnergyCoins(data.coinReveals ?? []);
            } else if (data.charge.kind === "MeterBonusIncrement") {
                // No Buffalo Energy Charge feature
                // Energy coins are revealed by mystery symbols
                BURFeatureModel.setMysteryEnergyCoins(data.coinReveals ?? []);
            }
        } else {
            // No energy charge feature is triggered
            // Dummy coins for entertainment
            BURFeatureModel.setMysteryEnergyCoins(data.coinReveals ?? []);
        }
    }

    parseBlueRespinData() {
        BURFeatureModel.setWillBaseGameFeatureTrigger(true);
        BURFeatureModel.setBlueRespinData(this.spinData!.replay!);
        const replayEnergyCharge =
            this.spinData!.replay!.featureInfo!.energyCharge;
        if (replayEnergyCharge) {
            BURFeatureModel.setReplayEnergyCoins(
                replayEnergyCharge.coinReveals ?? []
            );
        }
    }

    validateResults(results: ISpinData[], spinData: IVendorSpinData): void {
        // Computing the free spins win amounts and count.

        results.reduce((previous, current, index, array) => {
            return current;
        });
    }

    private resetFeatureData() {
        // Feature triggers
        BURFeatureModel.setWillBaseGameFeatureTrigger(false);
        BURFeatureModel.clearBaseGameFeaturesToTrigger();
        // Base game features
        BURFeatureModel.setRandomWildsPositions([]);
        BURFeatureModel.setEnergyCoins([]);
        BURFeatureModel.setReplayEnergyCoins([]);
        BURFeatureModel.setBlueRespinData(undefined);
        // JP
        BURJackpotsModel.setJackpotFragments([]);
        BURJackpotsModel.setJackpotWins([]);
    }

    protected processData(data): ISpinData[] | ISpinData {
        ConsoleUtil.warn("SPIN DATA", data);
        if (data) {
            if (DebugModel.fakeJackpot) {
                JackpotsModel.winAmount = DebugModel.fakeJackpot.amount;
                JackpotsModel.jackpotWonName = DebugModel.fakeJackpot.name;
                DebugModel.cleanUp();
            } else {
                if (
                    JackpotsModel.playForJackpot === true &&
                    data.data.jackpot?.won
                ) {
                    JackpotsModel.winAmount = Number(
                        data.data.jackpot.won.amount
                    );
                    JackpotsModel.jackpotWonName = data.data.jackpot.won.name;
                }
            }

            this.accumulatedWin = 0;
            this.totalFreeSpinsCount = 0;
            this.currentFreeSpinsCount = 0;
        }

        const spinData: IVendorSpinData =
            data.data.gameData ||
            data.data.lastSessionData.betResult.playData.gameData;
        let result: ISpinData = this.parseSlotObject(
            spinData,
            undefined,
            undefined,
            undefined,
            RoundType.NORMAL
        );
        let secondary: ISpinData = this.parseSlotObject(
            spinData,
            undefined,
            undefined,
            undefined,
            RoundType.NORMAL
        );

        // Reset the main spin free spins counts.
        // result.freespinsNum = 0;
        // result.freespinsWon = 0;
        // result.totalFreeSpins = 0;

        // Starting with the total free spins count, then we subtract the retriggers.
        let initialFreeSpinsCount = this.gameSpecificFreespinTotalPerDrop;
        spinData?.bonusInfo?.forEach((bonusData, index) => {
            if (bonusData.bonusName === "AvalancheResult") {
                bonusData.bonusInfo.forEach((nestedBonus, nbIndex) => {
                    if (nestedBonus?.freeSpinList && nbIndex === 1) {
                        // initialFreeSpinsCount = nestedBonus?.freeSpinList.length;
                    }
                    if (nestedBonus?.freeSpinList) {
                        // initialFreeSpinsCount -= nestedBonus.countFreeSpins;
                    }
                });
            }

            if (bonusData?.freeSpinList?.length) {
                bonusData.freeSpinList.forEach((spin) => {
                    spin?.bonusInfo?.forEach((bonus) => {
                        if (
                            bonus.bonusName == BonusName.BonusFreeSpins &&
                            Utils.isNumeric(bonus.countFreeSpins)
                        ) {
                            // initialFreeSpinsCount -= bonus.countFreeSpins;
                        }
                    });
                });
            }
        });
        // freespin selection screen
        spinData?.bonusInfo?.forEach((bonusData) => {
            if (
                bonusData.bonusName == BonusName.FreeSpinChoice &&
                !result?.isFree
            ) {
                result.freespinSelectScreen = true;
            } else {
                result.freespinSelectScreen = false;
            }
        });
        let results: ISpinData[] = [];
        results.push(result);

        spinData?.bonusInfo?.forEach((bonusData) => {
            if (bonusData.bonusName === "AvalancheResult") {
                bonusData.bonusInfo.forEach((nestedBonus, nbIndex) => {
                    if (nestedBonus?.freeSpinList) {
                        this.addRetriggerData(
                            nestedBonus,
                            result,
                            results,
                            initialFreeSpinsCount
                        );
                    }
                });
            }
            if (bonusData?.freeSpinList?.length) {
                // We have free spins awarded
                this.addRetriggerData(
                    bonusData,
                    result,
                    results,
                    initialFreeSpinsCount
                );
            } else if (bonusData?.reSpinsList) {
                // Adding the non fs respins
                this.addRespinData([bonusData], result, results, false);
            }
        });

        this.validateResults(results, spinData);
        if (!result.initial && App.config.isCascadeGame) {
            secondary.cascades?.forEach((cascade) => {
                if (cascade.cascadeFreespinCutoffIndex) {
                    secondary.cascades?.splice(
                        0,
                        cascade.cascadeFreespinCutoffIndex
                    );
                    // 0 out freespins so its not an endless loop
                    secondary.freespinsNum = 0;
                    secondary.freespinsWon = 0;
                    //0 out data to make sure end cascade doesnt have winline
                    secondary.winsData = [];
                    results.push(secondary);
                }
            });
        } else if (result.initial && App.config.isCascadeGame) {
            secondary.freespinsNum = 0;
            secondary.freespinsWon = 0;
            results.push(secondary);
        }
        return results;
    }
}
